// LEGACY: the panel as it was before the Capability release (main before FE #647),
// shown to orgs without the release (utils/capabilityRelease.ts). Not edited:
// delete it once the release is on for every org.
import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GapsRisksPanel, { GAPS_PANEL_WIDTH, TeamsSelect, type GapsRisksPanelProps } from './GapsRisksPanel';
import DevelopmentTierPanel from '../../../../../../components/gamification/DevelopmentTierPanelLegacy';
import DosenTooltip from '../../../../../../components/ui/DosenTooltip';
import { adminIntro, type MedalsData } from '../../../../../../components/gamification/MedalScore';
import { useAppSelector } from '../../../../../../redux/hooks';

export type InsightsView = 'gaps' | 'tier';

const BRANDY_FRAME = 'rgba(247, 148, 32, 0.45)';
const SURFACE = '#1C1C1C';

type Props = {
  view: InsightsView;
  onViewChange: (view: InsightsView) => void;
  /** The org's tier for the selected teams (Learning Gaps Closed first). */
  tierMedals: MedalsData | null;
  tierLoading: boolean;
  audience: 'orgAdmin' | 'groupAdmin';
} & Omit<GapsRisksPanelProps, 'embedded' | 'headerLeft'>;

/**
 * Learning Gaps / Learning Tier — two section titles in the same type as
 * LEARNING FRAMEWORK (all three are equal sections). The active one is white,
 * the other dimmed; the tier's explainer sits behind the (i) beside its title.
 */
const ViewTabs: React.FC<{ view: InsightsView; onChange: (view: InsightsView) => void }> = ({ view, onChange }) => {
  const orgName = useAppSelector((state) => state?.orgDetails?.data?.name) || '';
  const tab = (key: InsightsView, label: string) => {
    const on = view === key;
    return (
      <Box
        component="button"
        type="button"
        role="tab"
        aria-selected={on}
        onClick={() => onChange(key)}
        sx={{
          p: 0,
          pb: '3px',
          border: 'none',
          // Brandy underline marks the active view.
          borderBottom: `2px solid ${on ? '#F79420' : 'transparent'}`,
          bgcolor: 'transparent',
          cursor: 'pointer',
          fontFamily: 'Inter, sans-serif',
          fontSize: 17,
          fontWeight: 700,
          lineHeight: 1.2,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          color: on ? '#FDFDFD' : '#565656',
          transition: 'color 0.15s',
          '&:hover': { color: on ? '#FDFDFD' : '#B6B6B6' }
        }}
      >
        {label}
      </Box>
    );
  };
  return (
    <Box role="tablist" sx={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
      {tab('gaps', 'Learning Gaps')}
      {/* Subtle brandy divide between the two sections. */}
      <Box sx={{ width: '1px', height: 18, flexShrink: 0, bgcolor: 'rgba(247, 148, 32, 0.4)' }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {tab('tier', 'Learning Tier')}
        {view === 'tier' && (
          <DosenTooltip
            tapOnPhone
            placement="bottom-start"
            componentsProps={{ tooltip: { sx: { maxWidth: 420 } } }}
            title={
              <Box>
                {adminIntro(orgName).map((paragraph, i, all) => (
                  <Typography
                    key={paragraph.slice(0, 24)}
                    sx={{
                      fontFamily: 'Inter, sans-serif',
                      fontSize: 11.5,
                      lineHeight: 1.5,
                      color: '#B6B6B6',
                      mb: i === all.length - 1 ? 0 : '8px'
                    }}
                  >
                    {paragraph}
                  </Typography>
                ))}
              </Box>
            }
          >
            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#B6B6B6', cursor: 'pointer', display: 'block' }} />
          </DosenTooltip>
        )}
      </Box>
    </Box>
  );
};

/**
 * Admin dashboard left panel — Learning Gaps or the org's Learning Tier, in the
 * same brandy frame as the learner's tier panel and split off from the Learning
 * Framework. One Teams filter drives both views, so switching keeps the selection.
 */
const AdminInsightsPanel: React.FC<Props> = ({
  view,
  onViewChange,
  tierMedals,
  tierLoading,
  audience,
  ...gapsProps
}) => {
  const tabs = <ViewTabs view={view} onChange={onViewChange} />;
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SURFACE,
        border: `1px solid ${BRANDY_FRAME}`,
        borderRadius: '8px',
        boxShadow: '0 0 28px rgba(247, 148, 32, 0.07)'
      }}
    >
      {view === 'gaps' ? (
        <GapsRisksPanel {...gapsProps} embedded headerLeft={tabs} />
      ) : (
        <>
          {/* Same header row as the Gaps view, so the titles and filter don't move when switching. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              px: '14px',
              py: '10px',
              minHeight: 56,
              borderBottom: '1px solid #2A2A2A',
              flexShrink: 0
            }}
          >
            {tabs}
            <Box sx={{ flex: 1 }} />
            {gapsProps.teamOptions.length > 0 && (
              <TeamsSelect
                teamOptions={gapsProps.teamOptions}
                selectedTeamIds={gapsProps.selectedTeamIds}
                onTeamsChange={gapsProps.onTeamsChange}
              />
            )}
          </Box>
          <DevelopmentTierPanel
            embedded
            audience={audience}
            medals={tierMedals}
            loading={tierLoading}
            actionsByMeasure={{}}
            actionsLoading={false}
          />
        </>
      )}
    </Box>
  );
};

export { GAPS_PANEL_WIDTH };
export default AdminInsightsPanel;
